import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useReducedMotion } from 'framer-motion';
import {
  DndContext, DragOverlay, KeyboardSensor, PointerSensor, closestCenter,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, horizontalListSortingStrategy, sortableKeyboardCoordinates, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const TAB_MOTION = { duration: 220, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' };
const horizontalOnly = ({ transform }) => ({ ...transform, y: 0 });
const MODIFIERS = [horizontalOnly];
const TAB_FRAME = 'group relative flex h-8 min-w-0 max-w-[16rem] flex-1 basis-0 items-center overflow-hidden rounded-lg border text-sm';

function SortableTab({ tab, disabled, reduceMotion, getTabClassName, renderTab }) {
  const { setNodeRef, setActivatorNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: tab.id,
    disabled,
    transition: reduceMotion ? null : TAB_MOTION,
  });
  return <div
    ref={setNodeRef}
    data-dashboard-tab-id={tab.id}
    className={`${TAB_FRAME} ${getTabClassName(tab)}`}
    style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.25 : 1 }}
  >
    {renderTab(tab, { handleProps: { ...attributes, ...listeners, ref: setActivatorNodeRef, style: { touchAction: 'none' } } })}
  </div>;
}

/** Keep reordering visual until drop; routing, active tab and persistence stay in Header. */
export default function SortableDashboardTabs({ tabs, onReorder, getTabClassName, renderTab, children }) {
  const [draggedId, setDraggedId] = useState(null);
  const reduceMotion = useReducedMotion();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const draggedTab = tabs.find(tab => tab.id === draggedId);
  return <DndContext
    sensors={sensors}
    collisionDetection={closestCenter}
    modifiers={MODIFIERS}
    accessibility={{ screenReaderInstructions: { draggable: 'Press Space to pick up a tab, use Left and Right arrows to move it, then Space to drop or Escape to cancel.' } }}
    onDragStart={({ active }) => setDraggedId(active.id)}
    onDragCancel={() => setDraggedId(null)}
    onDragEnd={({ active, over }) => {
      setDraggedId(null);
      if (over && active.id !== over.id) onReorder(active.id, over.id);
    }}
  >
    <div className="hidden min-w-0 flex-1 items-center overflow-hidden lg:flex" role="tablist" aria-label="Dashboard tabs">
      <SortableContext items={tabs.map(tab => tab.id)} strategy={horizontalListSortingStrategy}>
        {tabs.map(tab => <SortableTab key={tab.id} tab={tab} disabled={tabs.length < 2} reduceMotion={reduceMotion} getTabClassName={getTabClassName} renderTab={renderTab} />)}
      </SortableContext>
      {children}
    </div>
    {createPortal(<DragOverlay dropAnimation={reduceMotion ? null : TAB_MOTION}>
      {draggedTab ? <div aria-hidden="true" className={`${TAB_FRAME} w-full border-edge bg-white text-ink shadow-paper-lg`}>
        {renderTab(draggedTab, { overlay: true, handleProps: { tabIndex: -1 } })}
      </div> : null}
    </DragOverlay>, document.body)}
  </DndContext>;
}
